"use client"

import { useEffect, useRef, useState } from "react"
import {
  Plus, Key, Copy, Eye, EyeOff, Trash2, Edit, MoreHorizontal,
} from "lucide-react"
import { motion } from "framer-motion"
import { Button } from "../ui/button"
import EmptyState from "../ui/EmptyState"
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../ui/table"
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../ui/dropdown-menu"
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "../ui/alert-dialog"
import { ApiKeyDialog } from "../api-key-dialog/api-key-dialog"
import { useToast } from "../../hooks/use-toast"
import { useOrganization } from "../../contexts/OrganizationContext"

import credentialService from "../../services/credential.service"
import { PRIMARY, BUTTON_SIZE } from '../../lib/buttonStyles';

function ApiKeys() {
  const { toast } = useToast()
  const { currentAccount } = useOrganization()
  const [apiKeys,     setApiKeys]     = useState([])
  const [loading,     setLoading]     = useState(true)
  const [refreshing,  setRefreshing]  = useState(false)
  const [busyId,      setBusyId]      = useState(null)
  const [dialogOpen,  setDialogOpen]  = useState(false)
  const [editingKey,  setEditingKey]  = useState(null)
  const [deleteId,    setDeleteId]    = useState(null)
  const [visibleKeys, setVisibleKeys] = useState(new Set())
  const [saving,      setSaving]      = useState(false)
  const firstLoad = useRef(true)

  // Guardar currentAccountId en localStorage para que credential service pueda usarlo
  useEffect(() => {
    if (currentAccount?.id) {
      localStorage.setItem('currentAccountId', currentAccount.id)
    }
  }, [currentAccount])

  useEffect(() => {
    if (currentAccount?.id) {
      reload(true)
    }
  }, [currentAccount])

  const reload = async (isInitial = false) => {
    if (isInitial) setLoading(true)
    else           setRefreshing(true)
    try {
      const result = await credentialService.listProviders()
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to load API keys')
      }
      
      // Obtener detalles de cada credencial
      const detailsPromises = result.data.map(async (cred) => {
        const detailResult = await credentialService.getCredential('provider', cred.id)
        return detailResult.success ? detailResult.data : cred
      })
      const details = await Promise.all(detailsPromises)
      
      const allowed = details
        .map((d) => ({
          id:        d.id,
          name:      d.description || d.name || `API Key`,
          key:       d.api_key,
          provider:  d.provider_id || d.provider_name,
          createdAt: (d.created_at || "").slice(0, 10),     
          lastUsed:  d.last_used   || "Never",
        }))
      setApiKeys(allowed)
    } catch (e) {
      toast({ title: "Error", description: e.message, variant: "destructive" })
    } finally {
      setLoading(false)
      setRefreshing(false)
      firstLoad.current = false
    }
  }

  const handleSave = async ({ name, key }) => {
    setSaving(true)
    try {
      // Si estamos editando, primero eliminar la credencial existente
      if (editingKey) {
        await credentialService.deleteCredential('provider', editingKey.id)
      }
      
      // Crear la nueva credencial con un provider_id genérico
      const result = await credentialService.createProvider({
        provider_id: 'custom',
        api_key: key.trim(),
        name: name.trim(),
        description: name.trim(),
      })
      
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to save API key')
      }
      
      toast({ 
        title: editingKey ? "Key actualizada" : "Key creada exitosamente",
        description: "Your API key has been saved securely."
      })
      await reload()
      
      // Solo cerrar si todo fue exitoso
      setDialogOpen(false)
      setEditingKey(null)
    } catch (e) {
      toast({ 
        title: "Error al guardar", 
        description: e.message, 
        variant: "destructive" 
      })
      // No cerrar el diálogo para que el usuario pueda corregir
    } finally {
      setSaving(false)
    }
  }

  const confirmDelete = async () => {
    if (!deleteId) return
    try {
      setBusyId(deleteId)
      const result = await credentialService.deleteCredential('provider', deleteId)
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to delete API key')
      }
      toast({ title: "Key eliminada" })
      await reload()                              
    } catch (e) {
      toast({ title: "Error", description: e.message, variant: "destructive" })
    } finally {
      setBusyId(null)
      setDeleteId(null)
    }
  }

  const toggleKeyVisibility = (id) => {
    const s = new Set(visibleKeys)
    s.has(id) ? s.delete(id) : s.add(id)
    setVisibleKeys(s)
  }

  const copyToClipboard = (key) => {
    navigator.clipboard.writeText(key)
    toast({ title: "API Key copiada", description: "Ya está en tu portapapeles." })
  }

  const maskKey = (key) =>
    key.length <= 12 ? key :
    `${key.slice(0, 8)}${"•".repeat(key.length - 12)}${key.slice(-4)}`

  return (
    <div className="space-y-6 p-4 min-h-screen sm:space-y-8 sm:p-8">
      {refreshing && (
        <div className="fixed top-4 right-4 z-50">
          <div className="animate-spin h-6 w-6 border-2 border-purple-500 border-t-transparent rounded-full" />
        </div>
      )}

      <div className="space-y-8">

        {/* Encabezado de la subsección y su acción, en la misma fila.
            "API Keys" ya no es un título de 30px: el título de la página es
            Credentials, y arriba está la pestaña que dice API Keys. Decirlo
            tres veces —pestaña, encabezado, y "API Keys Management" sobre la
            tabla— no aclaraba nada.
            El conteo va de insignia. Tenía una tarjeta de media pantalla
            para mostrar un número, y una sola métrica no es un panel. */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold text-white">API Keys</h2>
              {!loading && apiKeys.length > 0 && (
                <span className="rounded-full border border-white/10 px-2 py-0.5 text-xs text-gray-400">
                  {apiKeys.length}
                </span>
              )}
            </div>
            <p className="mt-1 text-sm text-gray-400">
              Secure access tokens for external services
            </p>
          </div>
          <Button
            onClick={() => { setEditingKey(null); setDialogOpen(true) }}
            className={`flex w-full items-center justify-center sm:w-auto ${PRIMARY}`}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add API Key
          </Button>
        </div>

        {/* Sin llaves no hay tabla que mostrar. Una tabla vacía con sus cinco
            encabezados y nada debajo se lee como una pantalla a medio cargar,
            no como "todavía no hay ninguna". */}
        {!loading && apiKeys.length === 0 ? (
          <EmptyState
            icon={<Key size={28} aria-hidden="true" />}
            title="No API keys yet"
            description="Create a key to let an external service authenticate as this account."
            action={{
              label: "Create API key",
              onClick: () => { setEditingKey(null); setDialogOpen(true) },
            }}
          />
        ) : (
        <div className="rounded-2xl border border-white/10 bg-[#0F1117] overflow-hidden">
          <div className="overflow-x-auto px-3 py-4 sm:px-6 sm:py-5">
            <Table className="w-full">
              <TableHeader>
                <TableRow>
                  <TableHead className="text-gray-400 font-semibold py-3">Name</TableHead>
                  <TableHead className="text-gray-400 font-semibold">Key</TableHead>
                  <TableHead className="text-gray-400 font-semibold">Created</TableHead>
                  <TableHead className="text-gray-400 font-semibold">Last Used</TableHead>
                  <TableHead className="text-gray-400 font-semibold">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-12">
                      <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-b-4 border-purple-500 mx-auto" />
                    </TableCell>
                  </TableRow>
                ) : (
                  apiKeys.map((k) => (
                    <TableRow key={k.id} className="hover:bg-white/5 transition group">
                      {/* Nombre */}
                      <TableCell className="font-medium text-white py-4">
                        <div className="flex items-center gap-3">
                          <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-[#7c3aed]/20">
                            <Key className="w-5 h-5 text-[#7c3aed]" />
                          </span>
                          <span className="font-bold">{k.name}</span>
                        </div>
                      </TableCell>
                      {/* Key + acciones */}
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <code className="font-mono text-sm bg-black/60 text-purple-300 px-3 py-2 rounded-lg border border-gray-800/50 backdrop-blur-xl">
                            {visibleKeys.has(k.id) ? k.key : maskKey(k.key)}
                          </code>
                          <Button variant="ghost" size="sm"
                            onClick={() => toggleKeyVisibility(k.id)}
                            className="hover:bg-black/40 text-slate-400 hover:text-white rounded-lg"
                          >
                            {visibleKeys.has(k.id) ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </Button>
                          <Button variant="ghost" size="sm"
                            onClick={() => copyToClipboard(k.key)}
                            className="hover:bg-black/40 text-slate-400 hover:text-white rounded-lg"
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                      {/* Fechas */}
                      <TableCell className="text-slate-400">{k.createdAt}</TableCell>
                      <TableCell className="text-slate-400">{k.lastUsed}</TableCell>
                      {/* Acciones */}
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="hover:bg-black/40 text-slate-300 hover:text-white rounded-xl">
                              <MoreHorizontal className="w-5 h-5" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-black/90 border border-gray-800 rounded-xl shadow-lg">
                            <DropdownMenuItem
                              className="text-slate-300 hover:bg-black/60 hover:text-white"
                              onClick={() => { setEditingKey(k); setDialogOpen(true) }}
                            >
                              <Edit className="w-4 h-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-slate-300 hover:bg-black/60 hover:text-white"
                              onClick={() => copyToClipboard(k.key)}
                            >
                              <Copy className="w-4 h-4 mr-2" />
                              Copy Key
                            </DropdownMenuItem>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <DropdownMenuItem
                                  className="text-red-400 hover:bg-red-900/60 hover:text-red-300"
                                  onSelect={(e) => e.preventDefault()}
                                  onClick={() => setDeleteId(k.id)}
                                >
                                  {busyId === k.id
                                    ? <span className="animate-spin h-4 w-4 mr-2 rounded-full border-2 border-current border-t-transparent"></span>
                                    : <Trash2 className="w-4 h-4 mr-2" />}
                                  Delete
                                </DropdownMenuItem>
                              </AlertDialogTrigger>
                              <AlertDialogContent className="bg-black/90 backdrop-blur-2xl border border-purple-500/20 rounded-3xl max-w-md">
                                <AlertDialogHeader>
                                  <AlertDialogTitle className="text-purple-300">
                                    Delete API Key
                                  </AlertDialogTitle>
                                  <AlertDialogDescription className="text-slate-400">
                                    This action is <span className="text-red-400 font-semibold">irreversible</span>.
                                    The application will no longer be able to use this key.
                                    Are you sure you want to continue?
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter className="mt-6 flex justify-end space-x-4">
                                  <AlertDialogCancel asChild>
                                    <Button variant="secondary">Cancel</Button>
                                  </AlertDialogCancel>
                                  <AlertDialogAction asChild>
                                    <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={confirmDelete}>
                                      Delete
                                    </Button>
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
        )}
        {/* Dialog alta / edición */}
        <ApiKeyDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          apiKey={editingKey}
          onSave={handleSave}
          saving={saving}
        />
      </div>
    </div>
  )
}

export default ApiKeys
